import React, { useState, useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { useIntersect } from 'global/hooks/useIntersect';
import { LinearProgress } from '@mui/material';
import NoData from './NoData';
import ErrorAlert from './ErrorAlert';
import { get, size, isEqual, concat, map } from 'global/lib/lodash';

/*
  This HOC takes an array of items and returns them as
  a list of the specified component type. It will load
  the list dynamically based on a step-count, scroll,
  and simple search criteria.

  Takes the following Components as args:
  * ListItemWrapped: The list item component to render
    (such as <ListItem /> for Material UI)
    This component will inherit passed through props
  * ListWrapped: the list item wrapper
    (such as <List /> for Material UI)

  Requires the following props:
  * data
    An array of the data necessary to load items

  Optional:
  * handlePageChange / currentPage / lastPage — omit for fully client-provided lists
  * loadWhileIntersecting
    After each page renders, request another page if more pages exist
    and the sentinel is still visible. One page at a time.
*/

const isSentinelVisible = (node) => {
  if (!node) {
    return false;
  }
  const rect = node.getBoundingClientRect();
  const viewHeight = window.innerHeight || document.documentElement.clientHeight;
  const viewWidth = window.innerWidth || document.documentElement.clientWidth;
  return rect.bottom > 0 && rect.right > 0 && rect.top < viewHeight && rect.left < viewWidth;
};

const withInfiniteScroll = (ListItemWrapped, ListWrapped) => {
  return withRouter((props) => {
    const {
      data,
      currentPage,
      handlePageChange,
      lastPage,
      loading,
      alwaysReplaceData,
      trackChecks,
      loadWhileIntersecting,
    } = props;
    const [lastData, setLastData] = useState(false);
    const [displayData, setDisplayData] = useState([]);
    // 'idle' | 'awaiting' — one in-flight page; recheck only after it renders.
    const phaseRef = useRef('idle');
    const renderedDataRef = useRef(null);
    const sentinelNodeRef = useRef(null);

    const [setIntersectNode, inView] = useIntersect({ rootMargin: '0px' });
    const setSentinelRef = (node) => {
      sentinelNodeRef.current = node;
      setIntersectNode(node);
    };
    // alwaysReplaceData parents (e.g. All Photos) update `data` only after the
    // page is ready to show — use it directly so settle sees the new list in
    // the same commit, not one effect later.
    const renderedData = alwaysReplaceData ? data || [] : displayData;
    // Without handlePageChange (e.g. Edition DocumentList), do not treat
    // missing lastPage as "load more" — that called a missing handler on filter.
    const hasMorePages = !!handlePageChange && (lastPage == null || Number(currentPage) < Number(lastPage));

    // Default: one page per intersection observer update (legacy).
    useEffect(() => {
      if (loadWhileIntersecting) {
        return;
      }
      if (get(inView, 'isIntersecting') === true && !loading) {
        handlePageChange?.(Number(currentPage) + 1);
      }
    }, [inView]);

    // Opt-in: while more pages exist, load one page at a time whenever the
    // sentinel is visible. After a request, wait until loading finishes and
    // the list renders, then re-measure the sentinel (IO entries can be stale).
    useEffect(() => {
      if (!loadWhileIntersecting) {
        return;
      }

      const listChanged = renderedDataRef.current !== renderedData;
      renderedDataRef.current = renderedData;

      if (loading) {
        phaseRef.current = 'awaiting';
        return;
      }

      if (phaseRef.current === 'awaiting') {
        if (!listChanged) {
          return;
        }
        phaseRef.current = 'idle';
        if (!hasMorePages || !isSentinelVisible(sentinelNodeRef.current)) {
          return;
        }
        phaseRef.current = 'awaiting';
        handlePageChange?.(Number(currentPage) + 1);
        return;
      }

      if (get(inView, 'isIntersecting') !== true || !hasMorePages) {
        return;
      }

      phaseRef.current = 'awaiting';
      handlePageChange?.(Number(currentPage) + 1);
    }, [inView, loading, renderedData, hasMorePages, loadWhileIntersecting, currentPage]);

    useEffect(() => {
      if (alwaysReplaceData) {
        setDisplayData(data);
      } else if (data && !isEqual(lastData, data) && !isEqual(displayData, data)) {
        const newData = currentPage === 1 ? data : concat(displayData, data);
        setDisplayData(newData);
      } else if (!data && !loading) {
        setDisplayData([]);
      }
      setLastData(data);
    }, [data, loading]);

    const showChecks = (() => {
      if (!trackChecks) {
        return;
      }

      if (size(renderedData) && size(renderedData)) {
        const newT = trackChecks.filter((nt) => nt.fromSelected).map((t) => t.id);
        return size(renderedData.filter((d) => newT.indexOf(d.id) > -1));
      } else {
        return false;
      }
    })();

    // PFM-2689: never spread row `onClick` (or other app props) onto the List
    // container. Spreading `{...props}` leaked router/filter/data fields onto the
    // DOM (`history`, `location`, `match`, `searchstring`, …) and also made List
    // click handlers fire with the raw event instead of the row item.
    // Pass presentation-only attrs via `listProps` when needed.
    const listProps = get(props, 'listProps', {}) || {};
    return (
      <>
        {props.header}
        {size(renderedData) ? (
          <>
            <div style={{ height: 4 }}>{loading && <LinearProgress />}</div>
            <ListWrapped {...listProps}>
              {map(renderedData, (d, index) => {
                const keyData = Array.isArray(d) ? d[0] : d;
                const keyIndex = props?.keyIndex && get(keyData, props.keyIndex) || keyData.id || index
                return (
                <ListItemWrapped path={props.location.pathname} index={index} key={keyIndex} item={d} showChecks={showChecks} {...props} />
              )})}
            </ListWrapped>
            {(hasMorePages || loading) && <div ref={setSentinelRef} style={{ height: 20 }}>{loading && <LinearProgress />}</div>}
          </>
        ) : get(props, 'error') ? (
          <ErrorAlert error={get(props, 'error')} />
        ) : (
          <NoData {...props} loading={loading} />
        )}
      </>
    );
  });
};

export default withInfiniteScroll;
